import { z } from 'zod'

import type { LedgerParams } from '@/hooks/queries/box-history'
import { codeList } from '@/lib/codes'
import { addDays, isoDay } from '@/lib/dates'

/**
 * The view state the box-history page reads from the URL.
 *
 * A window is *required* by the backend (v1 returned a warehouse's entire history
 * when none was named), so the page always has one: the URL's, or the last 30
 * days applied at read time. Defaults are applied there and not written into the
 * URL, the same rule as every list page.
 */
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)

export const boxHistorySearchSchema = z.object({
  warehouse: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The warehouse whose ledger is shown; nothing loads without it.'),
  from: isoDate.describe('The first day of the window, YYYY-MM-DD; 30 days before `to` if absent.'),
  to: isoDate.describe('The last day of the window, YYYY-MM-DD, inclusive; today if absent.'),
  boxes: z
    .string()
    .optional()
    .catch(undefined)
    .describe('Box codes to narrow to, comma-separated, exact.'),
  skus: z
    .string()
    .optional()
    .catch(undefined)
    .describe('SKU codes to narrow to, comma-separated, exact.'),
  tab: z
    .enum(['inward', 'outward', 'shift'])
    .optional()
    .catch(undefined)
    .describe('Which ledger is shown: inward, outward or shifts between boxes.'),
})

export type BoxHistorySearch = z.infer<typeof boxHistorySearchSchema>

/** Drop anything empty, so a URL carries only what was chosen. */
export function withoutSearchDefaults(search: BoxHistorySearch): BoxHistorySearch {
  const pruned: BoxHistorySearch = {}
  if (search.warehouse !== undefined) pruned.warehouse = search.warehouse
  if (search.from) pruned.from = search.from
  if (search.to) pruned.to = search.to
  if (search.boxes) pruned.boxes = search.boxes
  if (search.skus) pruned.skus = search.skus
  if (search.tab && search.tab !== 'inward') pruned.tab = search.tab
  return pruned
}

export const DEFAULT_WINDOW_DAYS = 30

/**
 * The window the page shows: the URL's, or the last 30 days ending today.
 * "Today" is the reader's local day: just after midnight east of UTC the UTC
 * date is still yesterday, and the window would end a day early.
 */
export function windowOf(
  search: BoxHistorySearch,
  now: Date = new Date(),
): { from: string; to: string } {
  const to = search.to ?? isoDay(now)
  const from = search.from ?? addDays(to, -DEFAULT_WINDOW_DAYS)
  return { from, to }
}

/** What the endpoint is asked for, or `undefined` while no warehouse is chosen. */
export function toLedgerParams(
  search: BoxHistorySearch,
  now: Date = new Date(),
): LedgerParams | undefined {
  if (search.warehouse === undefined) return undefined
  const { from, to } = windowOf(search, now)
  // The inclusive end date becomes the half-open bound the API takes: the first
  // instant of the day after.
  const end = new Date(`${to}T00:00:00.000Z`)
  end.setUTCDate(end.getUTCDate() + 1)
  return {
    warehouseId: search.warehouse,
    dateFrom: `${from}T00:00:00.000Z`,
    dateTo: end.toISOString(),
    boxCodes: codeList(search.boxes),
    skuCodes: codeList(search.skus),
  }
}
